import { EmptyState } from "@/components/EmptyState";
import { LoadingPanel } from "@/components/LoadingPanel";
import { StatCard } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useTenancy } from "@/hooks/useTenancy";
import { formatDateGB, formatPence, formatRentDueDay, nextRentDueDateUTC } from "@/lib/format";
import { Check, Copy } from "lucide-react";
import { useState } from "react";

export function Rent() {
  const { tenancies, loading } = useTenancy();
  if (loading) return <LoadingPanel />;
  if (tenancies.length === 0) return <EmptyState title="No active rent details on file" />;

  return (
    <div className="flex flex-col gap-6">
      <header>
        <span className="text-xs uppercase tracking-[0.18em] text-ink-dim">Rent</span>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mt-2">
          Rent &amp; payments
        </h1>
      </header>
      {tenancies.map(({ tenancy }) => (
        <RentBlock key={tenancy.id} tenancy={tenancy} />
      ))}
    </div>
  );
}

function RentBlock({ tenancy }: { tenancy: import("@/lib/supabase").Tenancy }) {
  const next = nextRentDueDateUTC(tenancy.rent_due_day);
  return (
    <div className="flex flex-col gap-6">
      <div className="grid sm:grid-cols-3 gap-4">
        <StatCard label="Monthly rent" value={formatPence(tenancy.monthly_rent_pence)} />
        <StatCard
          label="Rent due"
          value={formatRentDueDay(tenancy.rent_due_day)}
          hint={`Next on ${formatDateGB(next)}`}
        />
        <StatCard label="Deposit held" value={formatPence(tenancy.deposit_amount_pence)} />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Standing order details</CardTitle>
          <CardDescription>
            Set up your standing order with these details. The reference is specific to your tenancy
            — please use it exactly.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4">
          <DetailRow label="Account name" value={tenancy.rent_account_name} />
          <DetailRow label="Sort code" value={tenancy.rent_sort_code} />
          <DetailRow label="Account number" value={tenancy.rent_account_number} />
          <DetailRow label="Reference" value={tenancy.rent_payment_reference} highlight />
        </CardContent>
      </Card>
    </div>
  );
}

function DetailRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // ignore
    }
  }
  return (
    <div className="rounded-xl border border-line p-4 flex items-center justify-between gap-3">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-ink-dim">{label}</div>
        <div
          className={
            highlight ? "text-base font-mono text-accent mt-1" : "text-base font-mono text-ink mt-1"
          }
        >
          {value}
        </div>
      </div>
      <Button variant="ghost" size="sm" onClick={copy} aria-label={`Copy ${label}`}>
        {copied ? <Check size={14} /> : <Copy size={14} />}
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}
